import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import { Point, WindowSize } from '../utils/types';
import { generateInitAutoPath } from './autoGenerate';
import { MIN_PATH_TIME } from '../utils/constants';
import { calculatePath } from './beePathUtils';

interface GameData {
  points: Point[];
  totalAttempts: number;
  successfulAttempts: number;
  playerName: string | null;
  isSinglePlay: boolean;
  winMessage: string | null;
  loseMessage: string | null;
}

interface GameStats {
  totalAttempts: number;
  successfulAttempts: number;
}

class GameService {
  async loadGameData(id: string | undefined, windowSize: { width: number; height: number }): Promise<GameData | null> {
    if (!id) return null;

    if (id === 'auto') {
      return {
        points: generateInitAutoPath(MIN_PATH_TIME, windowSize.width, windowSize.height),
        totalAttempts: 0,
        successfulAttempts: 0,
        playerName: null,
        isSinglePlay: false,
        winMessage: null,
        loseMessage: null
      };
    }

    const docSnap = await getDoc(doc(db, 'paths', id));
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      points: data.points,
      totalAttempts: data.totalAttempts || 0,
      successfulAttempts: data.successfulAttempts || 0,
      playerName: data.playerName || null,
      isSinglePlay: !!data.isSinglePlay,
      winMessage: data.winMessage || null,
      loseMessage: data.loseMessage || null
    };
  }

  async loadGameStats(id: string | undefined): Promise<GameStats | null> {
    if (!id) return null;
    
    const docSnap = await getDoc(doc(db, 'paths', id));
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    return {
      totalAttempts: data.totalAttempts || 0,
      successfulAttempts: data.successfulAttempts || 0
    };
  }

  async incrementAttempts(id: string): Promise<void> {
    if (!id) return;
    await updateDoc(doc(db, 'paths', id), {
      totalAttempts: increment(1)
    });
  }

  async incrementSuccess(id: string): Promise<void> {
    if (!id) return;
    await updateDoc(doc(db, 'paths', id), {
      successfulAttempts: increment(1)
    });
  }

  isGameExpired(gameData: GameData): boolean {
    return gameData.isSinglePlay && (gameData.successfulAttempts > 0 || gameData.totalAttempts > 0);
  }

  async generatePath(windowSize: WindowSize): Promise<Point[]> {
    // Randomly choose a path type: straight, circular, or elliptical
    const pathType = Math.random() < 0.33 ? 'straight' : (Math.random() < 0.5 ? 'circular' : 'elliptical');
    
    // Randomly choose start and end positions from screen edges
    const edge = Math.floor(Math.random() * 4); // 0: top, 1: right, 2: bottom, 3: left
    // Initialize with default values
    let startX = 0, startY = 0, endX = 0, endY = 0;
    
    // Ensure start and end points are on different edges
    const endEdge = (edge + 2 + Math.floor(Math.random() * 2)) % 4;
    
    // Set start position based on edge
    switch (edge) {
      case 0: // top
        startX = Math.random() * windowSize.width;
        startY = Math.random() * windowSize.height * 0.2;
        break;
      case 1: // right
        startX = windowSize.width - Math.random() * windowSize.width * 0.2;
        startY = Math.random() * windowSize.height;
        break;
      case 2: // bottom
        startX = Math.random() * windowSize.width;
        startY = windowSize.height - Math.random() * windowSize.height * 0.2;
        break;
      case 3: // left
        startX = Math.random() * windowSize.width * 0.2;
        startY = Math.random() * windowSize.height;
        break;
    }
    
    // Set end position based on endEdge
    switch (endEdge) {
      case 0: // top
        endX = Math.random() * windowSize.width;
        endY = Math.random() * windowSize.height * 0.2;
        break;
      case 1: // right
        endX = windowSize.width - Math.random() * windowSize.width * 0.2;
        endY = Math.random() * windowSize.height;
        break;
      case 2: // bottom
        endX = Math.random() * windowSize.width;
        endY = windowSize.height - Math.random() * windowSize.height * 0.2;
        break;
      case 3: // left
        endX = Math.random() * windowSize.width * 0.2;
        endY = Math.random() * windowSize.height;
        break;
    }

    let controlPoints;
    switch (pathType) {
      case 'straight':
        // For straight paths, use control points that create a slight curve
        controlPoints = [
          { x: startX, y: startY },
          { 
            x: startX + (endX - startX) * 0.25 + (Math.random() - 0.5) * windowSize.width * 0.1,
            y: startY + (endY - startY) * 0.25 + (Math.random() - 0.5) * windowSize.height * 0.1
          },
          { 
            x: startX + (endX - startX) * 0.75 + (Math.random() - 0.5) * windowSize.width * 0.1,
            y: startY + (endY - startY) * 0.75 + (Math.random() - 0.5) * windowSize.height * 0.1
          },
          { x: endX, y: endY }
        ];
        break;
        
      case 'circular':
        // For circular paths, create a more pronounced curve
        const centerX = (startX + endX) / 2;
        const centerY = (startY + endY) / 2;
        // Increase radius to make circles larger and more obvious
        const radius = Math.min(windowSize.width, windowSize.height) * (0.3 + Math.random() * 0.2); // 0.3 to 0.5 of min dimension
        const angleOffset = Math.random() * Math.PI * 2;
        
        controlPoints = [
          { x: startX, y: startY },
          { 
            x: centerX + radius * Math.cos(angleOffset + Math.PI * 0.5),
            y: centerY + radius * Math.sin(angleOffset + Math.PI * 0.5)
          },
          { 
            x: centerX + radius * Math.cos(angleOffset + Math.PI * 1.5),
            y: centerY + radius * Math.sin(angleOffset + Math.PI * 1.5)
          },
          { x: endX, y: endY }
        ];
        break;
        
      case 'elliptical':
        // For elliptical paths, create an oval-like curve
        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;
        // Increase width and height factors for more pronounced ellipses
        const ellipseWidth = Math.abs(endX - startX) * (1.5 + Math.random() * 0.5); // 1.5 to 2.0
        const ellipseHeight = Math.abs(endY - startY) * (1.5 + Math.random() * 0.5); // 1.5 to 2.0
        const ellipseRotation = Math.random() * Math.PI * 2;
        
        controlPoints = [
          { x: startX, y: startY },
          { 
            x: midX + ellipseWidth * Math.cos(ellipseRotation) * 0.5 + (Math.random() - 0.5) * windowSize.width * 0.1,
            y: midY + ellipseHeight * Math.sin(ellipseRotation) * 0.5 + (Math.random() - 0.5) * windowSize.height * 0.1
          },
          { 
            x: midX + ellipseWidth * Math.cos(ellipseRotation + Math.PI) * 0.5 + (Math.random() - 0.5) * windowSize.width * 0.1,
            y: midY + ellipseHeight * Math.sin(ellipseRotation + Math.PI) * 0.5 + (Math.random() - 0.5) * windowSize.height * 0.1
          },
          { x: endX, y: endY }
        ];
        break;
    }

    // Calculate path points with timing
    const duration = 5000; // 5 seconds
    const points = calculatePath(controlPoints, duration);

    return points;
  }
}

export const gameService = new GameService(); 